import React from 'react';
import { Card, Space, Typography, Alert, Table, Tag, Statistic, Row, Col, Collapse, Timeline, Empty } from 'antd';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ClockCircleOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  MinusCircleOutlined,
  CodeOutlined
} from '@ant-design/icons';
import { ExecuteData, StepExecutionResult } from '../types';
import { MethodBadge } from '../Common';
import JsonViewer from '@src/components/Common/JsonViewer';
import moment from 'moment';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface ExecutionResultsViewerProps {
  data: ExecuteData;
}

const STATUS_CONFIG = {
  success: {
    icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
    color: 'success' as const,
    text: 'SUCCESS'
  },
  failed: {
    icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
    color: 'error' as const,
    text: 'FAILED'
  },
  skipped: {
    icon: <MinusCircleOutlined style={{ color: '#8c8c8c' }} />,
    color: 'default' as const,
    text: 'SKIPPED'
  }
};

const StepResultCard: React.FC<{ step: StepExecutionResult }> = ({ step }) => {
  const statusConfig = STATUS_CONFIG[step.status] || STATUS_CONFIG.success;
  const hasError = step.status === 'failed';
  const isSkipped = step.status === 'skipped';

  return (
    <Card
      size="small"
      style={{
        marginBottom: '12px',
        borderLeft: hasError ? '4px solid #ff4d4f' : isSkipped ? '4px solid #faad14' : '4px solid #52c41a',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
        transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
        background: hasError
          ? 'linear-gradient(135deg, #fff2f0 0%, #ffffff 100%)'
          : isSkipped
          ? 'linear-gradient(135deg, #fffbe6 0%, #ffffff 100%)'
          : 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {/* Header */}
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Tag color="blue">{step.step_index + 1}</Tag>
            <Text strong>{step.name}</Text>
          </Space>
          <Space>
            <Tag icon={statusConfig.icon} color={statusConfig.color}>
              {statusConfig.text}
            </Tag>
            <MethodBadge method={step.method} />
            {step.status_code && (
              <Tag color={step.status_code < 400 ? 'success' : 'error'}>
                HTTP {step.status_code}
              </Tag>
            )}
          </Space>
        </Space>

        {/* URL */}
        <div>
          <Text code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
            {step.url}
          </Text>
        </div>

        {/* Metrics */}
        <Space size="large">
          {step.duration_ms !== undefined && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <ClockCircleOutlined /> Duration: {step.duration_ms}ms
            </Text>
          )}
          {step.response_stored_as && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <CodeOutlined /> Stored as: {step.response_stored_as}
            </Text>
          )}
        </Space>

        {/* Error */}
        {hasError && step.error && (
          <Alert
            message="Error"
            description={step.error}
            type="error"
            showIcon
          />
        )}

        {/* Skipped reason */}
        {isSkipped && step.skipped_due_to_dependency_failure && (
          <Alert
            message="Skipped"
            description="This step was skipped due to failed dependencies"
            type="warning"
            showIcon
          />
        )}

        {/* Collapsible details */}
        <Collapse ghost>
          {step.body && (
            <Panel header="Request Body" key="body">
              <JsonViewer data={step.body} fontSize="13px" lineHeight="1.6" />
            </Panel>
          )}
          {(step.response_body || step.response) && (
            <Panel header="Response" key="response">
              <JsonViewer data={step.response_body || step.response} fontSize="13px" lineHeight="1.6" />
            </Panel>
          )}
        </Collapse>
      </Space>
    </Card>
  );
};

export const ExecutionResultsViewer: React.FC<ExecutionResultsViewerProps> = ({ data }) => {
  // Debug logging
  console.log('[ExecutionResultsViewer] Received data:', data);
  console.log('[ExecutionResultsViewer] Data type:', typeof data);
  console.log('[ExecutionResultsViewer] Data keys:', data ? Object.keys(data) : 'null');

  if (!data) {
    return (
      <Empty
        description="No execution data available"
        style={{ padding: '40px 0' }}
      />
    );
  }

  // Error boundary - check for required fields
  if (typeof data !== 'object') {
    return (
      <Alert
        message="Error Rendering Execution Data"
        description={`Expected object but received ${typeof data}. Data: ${JSON.stringify(data).slice(0, 200)}`}
        type="error"
        showIcon
      />
    );
  }

  // If data is empty or missing required fields, show empty state instead of error
  if (!data.steps || !Array.isArray(data.steps) || !data.summary) {
    return (
      <Empty
        description="No execution results yet"
        style={{ padding: '40px 0' }}
      />
    );
  }

  const overallSuccess = data.status === 'success';
  const startTime = moment(data.summary.start_time);
  const endTime = moment(data.summary.end_time);

  // Categorize steps
  const successSteps = data.steps.filter(s => s.status === 'success');
  const failedSteps = data.steps.filter(s => s.status === 'failed');
  const skippedSteps = data.steps.filter(s => s.status === 'skipped');

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Compact Execution Status */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #f0f0f0'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong style={{ fontSize: '14px', color: '#666' }}>Execution Result</Text>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size="small">
              <Tag color={overallSuccess ? 'success' : 'error'}>
                {data.status.toUpperCase().replace(/_/g, ' ')}
              </Tag>
              {failedSteps.length > 0 && (
                <Tag color="error">{failedSteps.length} Failed</Tag>
              )}
              {skippedSteps.length > 0 && (
                <Tag color="warning">{skippedSteps.length} Skipped</Tag>
              )}
              <Tag color="success">{successSteps.length} Success</Tag>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ID: {data.execution_id}
              </Text>
            </Space>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              Duration: <Text strong>{data.summary.duration_ms ? `${(data.summary.duration_ms / 1000).toFixed(2)}s` : 'N/A'}</Text>
            </Text>
          </div>
        </Space>
      </Card>

      {/* Step Results */}
      <Card title={`Step Results (${data.steps.length})`}>
        {/* Show failed steps first */}
        {failedSteps.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ color: '#ff4d4f' }}>
              <CloseCircleTwoTone twoToneColor="#ff4d4f" /> Failed Steps ({failedSteps.length})
            </Title>
            {failedSteps.map((step, idx) => (
              <StepResultCard key={idx} step={step} />
            ))}
          </div>
        )}

        {/* Successful steps */}
        {successSteps.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ color: '#52c41a' }}>
              <CheckCircleTwoTone twoToneColor="#52c41a" /> Successful Steps ({successSteps.length})
            </Title>
            <Collapse ghost>
              <Panel header="Show Details" key="success-steps">
                {successSteps.map((step, idx) => (
                  <StepResultCard key={idx} step={step} />
                ))}
              </Panel>
            </Collapse>
          </div>
        )}

        {/* Skipped steps */}
        {skippedSteps.length > 0 && (
          <div>
            <Title level={5} style={{ color: '#8c8c8c' }}>
              <MinusCircleOutlined /> Skipped Steps ({skippedSteps.length})
            </Title>
            <Collapse ghost>
              <Panel header="Show Details" key="skipped-steps">
                {skippedSteps.map((step, idx) => (
                  <StepResultCard key={idx} step={step} />
                ))}
              </Panel>
            </Collapse>
          </div>
        )}
      </Card>

      {/* Stored Responses */}
      {data.stored_responses && Object.keys(data.stored_responses).length > 0 && (
        <Card title="Stored Responses">
          <Collapse>
            {Object.entries(data.stored_responses).map(([key, value]) => (
              <Panel header={key} key={key}>
                <JsonViewer data={value} fontSize="13px" lineHeight="1.6" />
              </Panel>
            ))}
          </Collapse>
        </Card>
      )}
    </Space>
  );
};

export default ExecutionResultsViewer;
